/**
 * Earthworks helpers (EW-1): who may press what, how a refusal reads, the
 * status badge, and the holdback / unit labels.
 *
 * The holdback is labelled from meta.holdbackLabel ("Holdback" in CA,
 * "Retainage" in US) and a volume from meta.units. Neither is a literal here:
 * when meta did not arrive, the label is the neutral fallback, not a guess.
 */
import type { EwMeta, EwUnits } from '../types/earthworks';
import { fmtQty } from './display-labels';
import { formatCalendarDate, formatInstant } from './calendar-date';

// ─── Who may press what (mirrors fieldiq-api lib/ew-roles.ts) ────────────────
//
// For hiding a button, not for enforcement. A 403 that still arrives is shown
// as a refusal, never as success.

export const EW_READ_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher', 'field_worker', 'logistics'] as const;
export const EW_ADMIN_ROLES = ['admin', 'office_staff'] as const;
export const EW_DISPATCH_ROLES = ['admin', 'office_staff', 'supervisor', 'dispatcher'] as const;
export const EW_RATE_READ_ROLES = EW_DISPATCH_ROLES;
export const EW_RATE_WRITE_ROLES = EW_ADMIN_ROLES;

const inList = (list: readonly string[]) => (role: string | null | undefined) => !!role && list.includes(role);
/** Settings, customer settings, and site create / edit / delete. */
export const canAdminEw = inList(EW_ADMIN_ROLES);
/** Job create / edit / status / delete, crew and equipment. */
export const canDispatchEw = inList(EW_DISPATCH_ROLES);
export const canReadEwRates = inList(EW_RATE_READ_ROLES);
export const canWriteEwRates = inList(EW_RATE_WRITE_ROLES);

// ─── Labels from meta ────────────────────────────────────────────────────────

/**
 * The holdback's name for this tenant, from meta. A missing meta reads as
 * "Holdback / retainage" so the screen claims neither country.
 */
export const holdbackLabel = (meta: Pick<EwMeta, 'holdbackLabel'> | null | undefined): string =>
  meta?.holdbackLabel || 'Holdback / retainage';

export const ewUnits = (meta: EwMeta | null | undefined): Partial<EwUnits> => meta?.units ?? {};

// ─── Refusals ────────────────────────────────────────────────────────────────

export interface EwApiError {
  status: number | null;
  code: string | null;
  message: string | null;
  extra: Record<string, unknown>;
}

export function ewApiError(err: unknown): EwApiError {
  const r = (err as { response?: { status?: unknown; data?: { error?: unknown; code?: unknown } } })?.response;
  const d = r?.data;
  return {
    status: typeof r?.status === 'number' ? r.status : null,
    code: typeof d?.code === 'string' ? d.code : null,
    message: typeof d?.error === 'string' && d.error ? d.error : null,
    extra: d && typeof d === 'object' ? (d as Record<string, unknown>) : {},
  };
}

const jobNumbers = (x: Record<string, unknown>): string[] =>
  Array.isArray(x.jobs)
    ? (x.jobs as unknown[])
        .map((j) => (j && typeof j === 'object' ? (j as { jobNumber?: unknown; job_number?: unknown }) : null))
        .map((j) => (j ? j.jobNumber ?? j.job_number : null))
        .filter((n): n is string => typeof n === 'string' && n !== '')
    : [];

/** Every refusal code the EW-1 routes send, as one plain sentence. */
const EW_REFUSALS: Record<string, (x: Record<string, unknown>) => string> = {
  site_has_live_jobs: (x) => {
    const nums = jobNumbers(x);
    return nums.length
      ? `This site cannot be deleted while it has an open job (${nums.join(', ')}). Complete or cancel ${nums.length === 1 ? 'that job' : 'those jobs'} first.`
      : 'This site cannot be deleted while it has an open job. Complete or cancel it first.';
  },
  site_has_jobs: () => 'This site has jobs, so it cannot move to a different customer.',
  site_customer_mismatch: () => 'That site belongs to a different customer.',
  invalid_transition: (x) => {
    const allowed = Array.isArray(x.allowed) ? (x.allowed as string[]) : [];
    return allowed.length
      ? `This job cannot move to that status. From here it can go to: ${allowed.map((a) => ewJobStatusSkin(a).label).join(', ')}.`
      : 'This job cannot move to that status from where it is.';
  },
  job_closed: () => 'This job is completed or cancelled, so it can no longer be changed.',
  job_not_deletable: () => 'Only a draft or scheduled job can be deleted. Cancel this one instead.',
  job_number_contended: () => 'Another job took that number at the same moment. Try again.',
  already_on_crew: () => 'That person is already on the crew.',
  already_on_job: () => 'That unit is already on this job.',
  not_on_crew: () => 'You are not on this job\'s crew, so you cannot open it.',
  rate_overlap: () =>
    'Another rate with this code already covers some of these days for the same customer. End that rate first, or use a different code.',
  currency_not_settable: () => 'A rate takes the company currency. It cannot be set on the rate.',
  currency_missing: () => 'Set the company currency (CAD or USD) in Settings before adding rates.',
  markup_not_passthrough: () => 'A markup applies to a passthrough rate only. Turn on passthrough or clear the markup.',
  crew_role_not_labour: () => 'A crew role applies to a labour rate only.',
  equipment_not_equipment_kind: () => 'A unit or equipment type applies to an equipment or trucking rate only.',
  unit_and_type: () => 'A rate prices one unit or one equipment type, not both. Pick one.',
  not_found: () => 'That record no longer exists. It may have been deleted.',
  // EW-4 (fieldiq-api#1284): daily logs, rent-ins, locates, permits, hazmat, warnings.
  future_day: () => 'That day has not happened yet. A log can be written for today or an earlier day.',
  job_not_scheduled: () => 'This job is still a draft. The office schedules it before work is logged.',
  daily_log_exists: () => 'This job already has a log for that day. Open that log and change it instead.',
  user_not_on_crew: () => 'Hours can only be entered for people on this job\'s crew.',
  equipment_not_on_job: () => 'That unit is not on this job. Add it to the job\'s equipment first.',
  rent_in_not_on_rent: () => 'That rented-in unit is not on rent on this day.',
  rent_in_not_on_job: () => 'That rented-in unit belongs to another job.',
  meter_owned_only: () => 'An hour-meter reading is recorded for an owned unit only, not a rent-in.',
  check_unit_mismatch: () => 'That pre-use check is for a different unit.',
  check_not_pre_use: () => 'Only a pre-use check can be linked to a daily log.',
  check_wrong_day: (x) =>
    typeof x.checkDay === 'string' ? `That pre-use check was filed on ${ewDay(x.checkDay)}, not on this day.` : 'That pre-use check was filed on a different day.',
  duplicate_crew: () => 'The same person is on the log twice.',
  duplicate_unit: () => 'The same unit is on the log twice.',
  duplicate_photo: () => 'The same photo is on the log twice.',
  invalid_upload: () => 'A file did not finish uploading. Remove it and attach it again.',
  warning_not_shown: () => 'A warning you carried on past is no longer showing, so there was nothing to record. Save again.',
  invalid_rent_dates: () => 'The off-rent day cannot be before the on-rent day.',
  rent_in_hours_outside_dates: () => 'Hours are already logged for this unit outside those dates. Change the logs first.',
  rent_in_has_hours: () => 'Hours are logged against this rented-in unit, so it cannot be removed. Take it off rent instead.',
  invalid_locate_window: () => 'The locate\'s "valid to" day cannot be before its "valid from" day.',
  job_not_on_site: () => 'That job is on a different site.',
  invalid_permit_dates: () => 'The permit cannot expire before it was issued.',
  // EW-5 (fieldiq-api#1290): field tickets and change orders.
  ticket_exists: () => 'This job already has a live ticket for this work. Open that ticket instead.',
  log_already_ticketed: () => 'A daily log in this ticket is already on another live ticket.',
  change_order_line_billed: () => 'A change-order line is already on another live ticket.',
  duplicate_file: () => 'That file is already on this ticket.',
  ticket_frozen: () => 'This ticket can no longer change.',
  ticket_not_draft: () => 'This ticket has already been signed or voided.',
  ticket_empty: () => 'This ticket has no lines, so it cannot be signed.',
  ticket_unpriced: (x) => {
    const n = Array.isArray(x.lines) ? x.lines.length : 0;
    return n === 1
      ? 'One line has no rate. Price it or remove it before the ticket is signed.'
      : n > 1
        ? `${n} lines have no rate. Price or remove them before the ticket is signed.`
        : 'A line has no rate. Price or remove it before the ticket is signed.';
  },
  ticket_not_signed: () => 'The customer has not signed this ticket yet.',
  ticket_approved: () => 'This ticket is already approved.',
  ticket_void: () => 'This ticket is void.',
  ticket_invoiced: () => 'This ticket is on an invoice. Void the invoice first.',
  currency_unset: () => 'The company has no currency set (Settings → Regional & Tax), so this cannot be priced.',
  change_order_line: () => 'A change-order line bills at the price the customer approved. Remove it instead of editing it.',
  change_order_closed: () => 'This change order can no longer change.',
  change_order_empty: () => 'A change order with no lines cannot be sent or approved.',
  change_order_approved: () => 'This change order is approved. It is part of the job\'s price and cannot be voided.',
  change_order_void: () => 'This change order is already void.',
  invalid_email: () => 'That email address is not valid.',
  signature_required: () => 'Sign in the box, then press Confirm signature.',
  invalid_rate: () => 'That rate is not on this customer\'s card.',
  rate_currency: () => 'That rate is in a different currency from this ticket.',
  rate_not_in_effect: () => 'That rate is not in effect on this day.',
  passthrough_rate: () => 'That rate is a passthrough (cost plus markup). Type the line\'s price instead.',
  // EW-6 (fieldiq-api#1300): invoices.
  ticket_not_approved: () => 'Only an approved field ticket can be invoiced. Approve the ticket first.',
  ticket_already_invoiced: () => 'A ticket here is already on another invoice. Void that invoice to bill it again.',
  tickets_different_customers: () => 'These tickets belong to different customers. One invoice bills one customer.',
  tickets_mixed_currency: () => 'These tickets are in more than one currency. One invoice uses one currency.',
  tickets_different_jurisdictions: () =>
    'These tickets were worked in different provinces or states. One invoice covers one province or state.',
  holdback_needs_one_job: () =>
    'A job that takes holdback is tracked on its own. Invoice each job\'s tickets on a separate invoice.',
  ticket_line_unpriced: () => 'A ticket line has no price. Price it on the ticket before invoicing.',
  no_billable_tickets: () => 'This job has no approved field tickets waiting to be invoiced.',
  invoice_negative: () => 'These lines add up to less than zero. An invoice cannot bill a negative amount.',
  quote_not_accepted: () => 'Only an accepted quote can be converted to an invoice.',
  draw_exceeds_quote: (x) =>
    typeof x.remaining_pct === 'number'
      ? `That is more than is left on the quote. At most ${fmtNumber(x.remaining_pct)}% of it can still be invoiced.`
      : 'That is more than is left on the quote.',
  quote_line_unpriced: () => 'A line on the quote has no price. Price it on the quote before invoicing.',
  quote_has_no_lines: () => 'This quote has no lines to invoice.',
  deposit_exceeds_invoice: () =>
    'The deposit received is more than this invoice. Put more work on the invoice: the deposit is credited once, in full.',
  deposit_currency_mismatch: () => 'The deposit was received in a different currency from this invoice.',
  deposit_changed: () => 'The deposit changed while this invoice was being made. Try again.',
  holdback_already_released: () => 'This job\'s holdback has already been released on another invoice.',
  holdback_mixed_currency: () => 'This job\'s holdback was withheld in more than one currency, so it cannot be released on one invoice.',
  nothing_withheld: () => 'Nothing has been withheld on this job\'s invoices, so there is nothing to release.',
  holdback_released: (x) =>
    typeof x.invoice_number === 'string'
      ? `This invoice's holdback was released on ${x.invoice_number}. Void ${x.invoice_number} first.`
      : 'This invoice\'s holdback has been released. Void the release invoice first.',
  invoice_not_sent: () => 'This invoice has not been sent yet. Mark it sent first.',
  invoice_not_draft: () => 'Only a draft invoice can be changed or marked sent.',
  payment_exceeds_balance: (x) =>
    typeof x.balance_due === 'number'
      ? `That is more than the ${fmtMoney(x.balance_due, null)} still owing on this invoice.`
      : 'That is more than is still owing on this invoice.',
  external_ref_used: () => 'That payment reference is already recorded. Refresh to see it.',
  invoice_has_payments: () => 'This invoice has payments recorded against it, so it cannot be voided.',
  invoice_void: () => 'This invoice is void.',
  invoice_already_void: () => 'This invoice is already void.',
  invoice_not_voidable: () => 'A paid invoice cannot be voided.',
  invoice_already_paid: () => 'This invoice is already paid in full.',
  void_reason_required: () => 'Say why the invoice is being voided.',
  invoice_number_contended: () => 'Another invoice took that number at the same moment. Try again.',
  invoice_state_changed: () => 'This invoice changed while this was being saved. Refresh and try again.',
  tax_unconfigured: () => 'Tax is not set up for this province or state. Set it in Settings → Regional & Tax, then try again.',
  tax_fallback_blocked: () =>
    'This invoice was taxed from the company default, not a rate for its province or state. Add the rate in Settings → Regional & Tax, void this invoice and create it again.',
};

export const EW_REFUSAL_CODES = Object.keys(EW_REFUSALS);

/**
 * A write's (or read's) error as one plain sentence. A coded refusal reads from
 * the map; a bare 404 reads as gone; a 403 is a permission refusal; anything
 * else uses the API's message, then `fallback`.
 */
export function ewRefusal(err: unknown, fallback: string): string {
  const e = ewApiError(err);
  if (e.code && EW_REFUSALS[e.code]) return EW_REFUSALS[e.code](e.extra);
  if (e.status === 404) return EW_REFUSALS.not_found({});
  if (e.status === 403) return 'You do not have permission to do this. Ask an admin or office staff.';
  return e.message ?? fallback;
}

// ─── Display ─────────────────────────────────────────────────────────────────

/** A number as given, grouped; absent → `—`. A real 0 is `0`. */
export const fmtNumber = (n: number | null | undefined): string => fmtQty(n, 6);

/** `120 m³`; absent value → `—`; absent unit → the number alone. */
export function withUnit(n: number | null | undefined, unit: string | null | undefined): string {
  const v = fmtNumber(n);
  if (v === '—') return v;
  return unit ? `${v} ${unit}` : v;
}

/** A money figure with the currency code after it; absent → `—`. */
export function fmtMoney(n: number | null | undefined, currency: string | null | undefined): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '—';
  const v = n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return currency ? `${v} ${currency}` : v;
}

/** A percentage; absent → `—`. */
export const fmtPct = (n: number | null | undefined): string => {
  const v = fmtNumber(n);
  return v === '—' ? v : `${v}%`;
};

/** A calendar day as 'Oct 26, 2026'; never through `new Date('YYYY-MM-DD')`. Absent → `—`. */
export const ewDay = (value: string | null | undefined): string => formatCalendarDate(value) ?? '—';

/** The label the API's catalog gives a value; the raw value if the catalog lacks it; `—` when absent. */
export function catalogLabel(list: readonly { value: string; label: string }[] | undefined, value: string | null | undefined): string {
  if (!value) return '—';
  return list?.find((e) => e.value === value)?.label ?? value;
}

export const blankToNull = (s: string): string | null => (s.trim() === '' ? null : s.trim());
export const toInput = (v: number | string | null | undefined): string => (v === null || v === undefined ? '' : String(v));

export interface EwSkin {
  label: string;
  fill: string;
  rail: string;
}

export const EW_UNKNOWN_SKIN: EwSkin = { label: '—', fill: 'bg-fi-signal-slate', rail: 'border-l-transparent' };

export const EW_STATUS_SKIN: Record<string, EwSkin> = {
  active: { label: 'Active', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  inactive: { label: 'Inactive', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ewStatusSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(EW_STATUS_SKIN, status) ? EW_STATUS_SKIN[status] : EW_UNKNOWN_SKIN;

/** The job status machine (mirrors fieldiq-api lib/ew-jobs) — for which buttons to show only. */
export const EW_JOB_TRANSITIONS: Record<string, readonly string[]> = {
  draft: ['scheduled', 'cancelled'],
  scheduled: ['draft', 'in_progress', 'cancelled'],
  in_progress: ['completed', 'cancelled'],
  completed: [],
  cancelled: [],
};
export const ewNextStatuses = (status: string | null | undefined): readonly string[] =>
  status && Object.prototype.hasOwnProperty.call(EW_JOB_TRANSITIONS, status) ? EW_JOB_TRANSITIONS[status] : [];
export const ewJobDeletable = (status: string | null | undefined) => status === 'draft' || status === 'scheduled';
export const ewJobClosed = (status: string | null | undefined) => status === 'completed' || status === 'cancelled';

export const EW_JOB_STATUS_SKIN: Record<string, EwSkin> = {
  draft: { label: 'Draft', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
  scheduled: { label: 'Scheduled', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  in_progress: { label: 'In progress', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  completed: { label: 'Completed', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  cancelled: { label: 'Cancelled', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ewJobStatusSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(EW_JOB_STATUS_SKIN, status) ? EW_JOB_STATUS_SKIN[status] : EW_UNKNOWN_SKIN;

// ─── Rate card ───────────────────────────────────────────────────────────────

/** Every measured price unit, by what it measures (mirrors fieldiq-api EW_PRICE_UNITS). */
const MEASURED_UNITS: Record<keyof EwUnits, readonly string[]> = {
  volume: ['m3', 'yd3'],
  area: ['m2', 'ft2'],
  length: ['m', 'ft'],
  distance: ['km', 'mi'],
  mass: ['tonne', 'ton'],
};

/**
 * The price units this tenant bills in: a measured unit (volume, area, length,
 * distance, mass) only in the tenant's system per meta.units; hour, day, each,
 * load and flat always. With no meta.units every unit stays, so nothing is
 * hidden on a guess. A rate already on the other unit keeps it selectable.
 */
export function ewPriceUnits(
  list: readonly { value: string; label: string }[] | undefined,
  meta: Pick<EwMeta, 'units'> | null | undefined,
  keep?: string | null,
): { value: string; label: string }[] {
  const units = meta?.units;
  if (!list) return [];
  if (!units) return [...list];
  const off = new Set<string>();
  (Object.keys(MEASURED_UNITS) as (keyof EwUnits)[]).forEach((k) =>
    MEASURED_UNITS[k].forEach((u) => {
      if (u !== units[k]) off.add(u);
    }),
  );
  return list.filter((u) => !off.has(u.value) || u.value === keep);
}

/** `95.00 CAD · Per hour`; absent rate → `—`. */
export function ewRateText(rate: number | null | undefined, currency: string | null | undefined, unitLabel: string | null | undefined): string {
  const v = fmtMoney(rate, currency);
  if (v === '—') return v;
  return unitLabel ? `${v} · ${unitLabel}` : v;
}

/** The rate's kind may name a unit or an equipment type (mirrors the API's equipment_not_equipment_kind). */
export const ewRateTakesEquipment = (kind: string | null | undefined) => kind === 'equipment' || kind === 'trucking';

// ─── EW-4: locates, rent-ins, warnings ───────────────────────────────────────

export const EW_LOCATE_SKIN: Record<string, EwSkin> = {
  valid: { label: 'Valid', fill: 'bg-fi-signal-green', rail: 'border-l-fi-green' },
  upcoming: { label: 'Not yet valid', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  expired: { label: 'Expired', fill: 'bg-fi-signal-red', rail: 'border-l-fi-red' },
};
export const ewLocateSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(EW_LOCATE_SKIN, status) ? EW_LOCATE_SKIN[status] : EW_UNKNOWN_SKIN;

export const EW_RENT_SKIN: Record<string, EwSkin> = {
  upcoming: { label: 'Upcoming', fill: 'bg-fi-signal-blue', rail: 'border-l-fi-blue' },
  on_rent: { label: 'On rent', fill: 'bg-fi-signal-amber', rail: 'border-l-fi-amber' },
  off_rent: { label: 'Off rent', fill: 'bg-fi-signal-slate', rail: 'border-l-fi-steel' },
};
export const ewRentSkin = (status: string | null | undefined): EwSkin =>
  status && Object.prototype.hasOwnProperty.call(EW_RENT_SKIN, status) ? EW_RENT_SKIN[status] : EW_UNKNOWN_SKIN;

/** An instant as 'Oct 26, 7:05 AM' in the tenant's zone (formatInstant); absent → `—`. */
export const ewInstant = (value: string | null | undefined, timeZone: string | null | undefined): string =>
  formatInstant(value, timeZone) ?? '—';

/** The PUT's meter_below_previous warning, as a sentence (the save went through). */
export function ewMeterWarningText(
  w: { kind: string; entered?: number; previous?: number; previous_date?: string },
  unitName: string | null,
): string {
  if (w.kind === 'meter_below_previous') {
    return `${unitName ?? 'A unit'}: the hour meter (${fmtNumber(w.entered)}) is below the last reading (${fmtNumber(w.previous)} on ${ewDay(w.previous_date)}). The log was saved; check the reading.`;
  }
  return 'The log was saved with a warning.';
}
